import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { getStatusColor } from '../style/index';
import StatusIcon from '../atoms/icons/StatusIcon';
import InputLabel from './InputLabel';
import { IRadioButtonGroupProps } from './types/RadioButtonGroup';
import { CustomStyledProps } from 'style/style';

const StatusMessageContainer = styled('div')`
  display: flex;
  align-items: center;
  position: absolute;
  margin-top: 4px;
`;

const SubLabelContainer = styled('span')`
  color: #595959;
  leading-trim: both;
  text-edge: cap;
  font-size: 13px;
  font-style: italic;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: -0.26px;
`;

const StatusMessage = styled('div')(
  ({ status }: CustomStyledProps) =>
    status && {
      color: getStatusColor()[status],
      paddingLeft: 4,
    }
);

const ButtonGroupStyle = styled('div')<{ newCard: boolean }>(
  ({ newCard }) =>
    newCard && {
      marginBottom: newCard ? 0 : 25,
      '&: > div > label': {
        marginBottom: 16,
      },
      '&: > div:last-of-type > label': {
        marginBottom: 0,
      },
    }
);

const ButtonGroup = styled('div')(props => ({
  marginBottom: '14px',
  position: 'relative',
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  border: `1px solid ${props.status !== 'default' ? statusColor[props.status] : 'transparent'}`,
  '& > div > label': {
    marginBottom: '16px',
  },
  '& > div:last-of-type > label': {
    marginBottom: '0',
  },
}));

const renderStatusMessage = (status, statusMessage) => {
  return (
    <StatusMessageContainer>
      <StatusIcon status={status} />
      <StatusMessage status={status}>{statusMessage}</StatusMessage>
    </StatusMessageContainer>
  );
};

const setTooltip = (tooltip: boolean) => (tooltip ? tooltip : null);

const RadioButtonGroup = ({
  inputLabel,
  labelDescription,
  status,
  statusMessage,
  tooltip,
  children,
  newCard = false,
  joinForm,
  customStyle,
  isRequired = false,
  customInputLabelStyle,
  subLabel,
  inlineSubLabel = false,
  customRadioListStyle,
}: IRadioButtonGroupProps): ReactElement => {
  const getCustomContainerStyle = (): React.CSSProperties | undefined => {
    if (subLabel && inlineSubLabel) {
      return { ...customStyle, ...{ display: 'inline' } };
    }

    return joinForm && !customInputLabelStyle ? { marginBottom: 0, fontSize: 15 } : customInputLabelStyle;
  };

  return (
    <div>
      {inputLabel ? (
        <InputLabel
          label={inputLabel}
          customContainerStyle={getCustomContainerStyle()}
          customStyle={subLabel && inlineSubLabel ? { ...customStyle, ...{ marginRight: 1 } } : customStyle}
          labelDescription={labelDescription}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          tooltip={setTooltip(tooltip)}
          isRequired={isRequired}
        />
      ) : null}
      {subLabel ? (
        <SubLabelContainer style={{ paddingLeft: inlineSubLabel ? 0 : 2 }}>{subLabel}</SubLabelContainer>
      ) : null}
      {joinForm ? (
        <ButtonGroup
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          status={status}
          style={customRadioListStyle}
        >
          {children}
        </ButtonGroup>
      ) : (
        <ButtonGroupStyle newCard={newCard} style={customRadioListStyle}>
          {children}
        </ButtonGroupStyle>
      )}
      {statusMessage && status !== 'default' ? renderStatusMessage(status, statusMessage) : null}
    </div>
  );
};

RadioButtonGroup.propTypes = {
  /** Label of radio button group (optional) */
  inputLabel: PropTypes.string,
  /** Label description (optional) */
  labelDescription: PropTypes.string,
  /** Tooltip next to label */
  tooltip: PropTypes.shape({
    icon: PropTypes.any,
    text: PropTypes.string,
    overlayStyle: PropTypes.object,
    placement: PropTypes.oneOf(['left', 'right', 'top', 'bottom', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight']),
  }),
  /** Determines if the radio group will be outlined with a specfic status color border (transparent for default status) */
  status: PropTypes.oneOf(['default', 'success', 'caution', 'error']),
  /** Message at bottom of radio group to describe status */
  statusMessage: PropTypes.string,
};

RadioButtonGroup.defaultProps = {
  status: 'default',
};

export default RadioButtonGroup;
